/**
 * 액체 열팽창(Hydraulic expansion) 시 소요 분출용량 산정.
 *
 * 근거: API Standard 521, 2020년 6월판 4.4.12.3
 *
 *   식 (2)  SI :  q = (αv × φ) / (1000 × d × c)
 *
 *     q   : 배출 조건에서의 체적유량 (m³/s)
 *     αv  : 배출 조건에서 액체의 체적팽창계수 (1/℃) — Table 2
 *     φ   : 총 전열량 (W). 열교환기는 운전 중 최대 duty 를 쓴다.
 *     d   : 물 기준 비중 (15.6 ℃ 물 = 1.00), 무차원
 *     c   : 갇힌 액체의 비열 (J/kg·K)
 *     1000: 15.6 ℃ 물의 밀도 (kg/m³)
 *
 * ⚠ 4.4.12.2 — 소요 유량이 아주 작아 실무에서는 DN 20 × DN 25
 *   (NPS 3/4 × NPS 1) 밸브를 관례적으로 쓴다. 이 계산은 그 크기로
 *   부족하다고 볼 이유가 있을 때 적용한다.
 */

/** API 521 Table 2 — 15.6 ℃ 에서의 체적팽창계수 (1/℃). */
export const CUBIC_EXPANSION_TABLE = [
  { label: "3 ~ 34.9 °API", minApi: 3, maxApi: 34.9, alphaV: 0.00072 },
  { label: "35 ~ 50.9 °API", minApi: 35, maxApi: 50.9, alphaV: 0.0009 },
  { label: "51 ~ 63.9 °API", minApi: 51, maxApi: 63.9, alphaV: 0.00108 },
  { label: "64 ~ 78.9 °API", minApi: 64, maxApi: 78.9, alphaV: 0.00126 },
  { label: "79 ~ 88.9 °API", minApi: 79, maxApi: 88.9, alphaV: 0.00144 },
  { label: "89 ~ 93.9 °API", minApi: 89, maxApi: 93.9, alphaV: 0.00153 },
  { label: "94 °API 이상 (경질)", minApi: 94, maxApi: Infinity, alphaV: 0.00162 },
  { label: "물", minApi: null, maxApi: null, alphaV: 0.00018 },
] as const;

/** 15.6 ℃ 물의 밀도 (kg/m³) — 식 (2)의 상수 1000. */
export const WATER_DENSITY_15_6C = 1000;

/**
 * °API 도로 Table 2 의 체적팽창계수를 찾는다.
 * 표에 없는 범위(°API < 3)면 null.
 */
export function cubicExpansionFromApiGravity(apiGravity: number): number | null {
  for (const row of CUBIC_EXPANSION_TABLE) {
    if (row.minApi === null || row.maxApi === null) continue;
    if (apiGravity >= row.minApi && apiGravity <= row.maxApi) return row.alphaV;
  }
  return null;
}

export interface ThermalExpansionInput {
  /** αv — 체적팽창계수 (1/℃). Table 2 또는 공정 설계자료. */
  readonly cubicExpansionCoefficient: number;
  /** φ — 총 전열량 (W). 열교환기는 최대 duty. */
  readonly heatTransferRateW: number;
  /** d — 물 기준 비중 (15.6 ℃ 물 = 1.00) */
  readonly relativeDensity: number;
  /** c — 갇힌 액체의 비열 (J/kg·K) */
  readonly specificHeatJKgK: number;
}

export interface ThermalExpansionSuccess {
  readonly ok: true;
  /** q — 체적유량 (m³/s) */
  readonly volumeFlowM3S: number;
  /** 체적유량 (L/min) — 액체 사이징식에 바로 넣는 단위 */
  readonly volumeFlowLpm: number;
  /** 질량유량 (kg/h) — 요약표 비교용 */
  readonly massFlowKgH: number;
  readonly equation: "API 521 (2)";
  readonly warnings: readonly string[];
}

export interface ThermalExpansionFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type ThermalExpansionResult =
  | ThermalExpansionSuccess
  | ThermalExpansionFailure;

/** 액체 열팽창 소요 분출용량 — API 521 식 (2). */
export function thermalExpansionRelievingRate(
  input: ThermalExpansionInput,
): ThermalExpansionResult {
  const {
    cubicExpansionCoefficient: alphaV,
    heatTransferRateW: phi,
    relativeDensity: d,
    specificHeatJKgK: c,
  } = input;

  const checks: ReadonlyArray<readonly [string, number]> = [
    ["체적팽창계수 αv", alphaV],
    ["총 전열량 φ", phi],
    ["비중 d", d],
    ["비열 c", c],
  ];
  for (const [label, v] of checks) {
    if (!Number.isFinite(v) || v <= 0) {
      return {
        ok: false,
        reason: "invalid-input",
        message: `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`,
      };
    }
  }

  const volumeFlowM3S = (alphaV * phi) / (WATER_DENSITY_15_6C * d * c);
  // 액체 밀도 = 비중 × 15.6 ℃ 물의 밀도 (표준은 압축성을 무시한다)
  const liquidDensityKgM3 = d * WATER_DENSITY_15_6C;

  const warnings: string[] = [
    "표준은 이 유량이 매우 작아 실무에서 DN 20 × DN 25(NPS 3/4 × NPS 1) 밸브를 " +
      "관례적으로 쓴다고 안내합니다. 그 크기로 부족할 근거가 있을 때 이 계산을 " +
      "적용하십시오(4.4.12.2).",
    "설정압은 보호 대상 계통에서 가장 약한 부품의 허용압을 넘지 않되, 열팽창 " +
      "조건에서만 열릴 만큼 높아야 합니다(4.4.12.2).",
  ];

  if (d > 1.2 || d < 0.4) {
    warnings.push(
      `비중 ${d} 는 통상 범위를 벗어납니다. 15.6 ℃ 물 기준값인지 확인하십시오.`,
    );
  }

  return {
    ok: true,
    volumeFlowM3S,
    volumeFlowLpm: volumeFlowM3S * 60_000,
    massFlowKgH: volumeFlowM3S * liquidDensityKgM3 * 3600,
    equation: "API 521 (2)",
    warnings,
  };
}
